// The insight engine: sensed sky + local clock -> next-best-action cards.
// Voice rules for new copy: imperative, specific, sentence-cased tags, never the
// word "now", no em dashes, one doable ask per card.
import type { Mood } from '../types';
import type { DeckCard, OpenMeteoForecastResponse, Wx } from './types';

/** Parses "2026-07-18T18:49" wall-clock strings without timezone damage. */
const wallMinutes = (iso: string): number => {
  const t = iso.split('T')[1].split(':');
  return Number(t[0]) * 60 + Number(t[1]);
};

const WMO: Record<number, string> = {
  0: 'clear sky',
  1: 'mostly clear',
  2: 'partly cloudy',
  3: 'overcast',
  45: 'fog',
  48: 'icy fog',
  51: 'light drizzle',
  53: 'drizzle',
  55: 'heavy drizzle',
  56: 'freezing drizzle',
  57: 'freezing drizzle',
  61: 'light rain',
  63: 'rain',
  65: 'heavy rain',
  66: 'freezing rain',
  67: 'freezing rain',
  71: 'light snow',
  73: 'snow',
  75: 'heavy snow',
  77: 'snow grains',
  80: 'showers',
  81: 'showers',
  82: 'heavy showers',
  85: 'snow showers',
  86: 'snow showers',
  95: 'a thunderstorm',
  96: 'a hailstorm',
  99: 'a hailstorm',
};

export function buildWx(w: OpenMeteoForecastResponse, city: string): Wx {
  const c = w.current;
  const d = w.daily;
  const code = c.weather_code;
  const isRain = (code >= 51 && code <= 67) || (code >= 80 && code <= 82);
  const isSnow = (code >= 71 && code <= 77) || code === 85 || code === 86;
  const isStorm = code >= 95;
  const isFog = code === 45 || code === 48;
  const daylightS = d.daylight_duration[0];
  // Polar day/night: sunrise==sunset markers make sun-anchored copy meaningless.
  const polar =
    d.sunrise[0] === d.sunset[0] || daylightS <= 60 || daylightS >= 86340;
  const sunriseM = wallMinutes(d.sunrise[0]);
  let sunsetM = wallMinutes(d.sunset[0]);
  // Sunset lands past midnight at high latitudes; this wraps it back for display.
  if (sunsetM < sunriseM) sunsetM += 1440;
  const desc = WMO[code] || 'sky';

  return {
    city,
    tz: w.timezone,
    temp: Math.round(c.temperature_2m),
    feels: Math.round(c.apparent_temperature),
    humidity: c.relative_humidity_2m,
    cloud: c.cloud_cover,
    wind: Math.round(c.wind_speed_10m),
    uv: Math.round(c.uv_index),
    uvMax: Math.round(d.uv_index_max[0]),
    code,
    desc,
    stripDesc: desc.replace(/^an? /, ''),
    isDay: c.is_day === 1,
    isRain,
    isSnow,
    isStorm,
    isFog,
    isClear: code <= 1,
    isOvercast: code === 3 || isFog,
    polar,
    nowM: wallMinutes(c.time),
    sunriseM,
    sunsetM,
    sunrise2M: wallMinutes(d.sunrise[1] || d.sunrise[0]),
    daylightS,
  };
}

// Deliberately NOT time-of-day-aware: the orb keeps the day-family palettes at
// night, which is why `nightClear`/`nightCloud` are unreachable from here. Only
// the sphere's look is frozen — buildDeck still uses isDay/sunrise for copy.
export function moodFor(x: Wx | null): Mood {
  if (!x) return 'default';
  if (x.isStorm) return 'storm';
  if (x.isSnow) return 'snow';
  if (x.isRain) return 'rain';
  if (x.isFog) return 'fog';
  if (x.feels >= 35) return 'hot';
  if (x.isOvercast) return 'overcast';
  if (x.code === 2) return 'partly';
  return 'clearDay';
}

/** All gating runs on the sky's wall clock when it exists, else the device's. */
export function localNowMinutes(wx: Wx | null, wxAt: number): number {
  if (wx) {
    return (wx.nowM + Math.round((performance.now() - wxAt) / 60000)) % 1440;
  }
  const n = new Date();
  return n.getHours() * 60 + n.getMinutes();
}

export const fmtM = (minute: number): string => {
  const wrapped = ((minute % 1440) + 1440) % 1440;
  let h = Math.floor(wrapped / 60);
  const mm = wrapped % 60;
  const ap = h >= 12 ? 'pm' : 'am';
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${String(mm).padStart(2, '0')} ${ap}`;
};

export const fmtDaylight = (seconds: number): string => {
  let h = Math.floor(seconds / 3600);
  let m = Math.round((seconds % 3600) / 60);
  if (m === 60) {
    h += 1;
    m = 0;
  }
  return `${h}h ${String(m).padStart(2, '0')}m`;
};

/** App-style sentence casing for the orb tags (single line, so mostly a safety net). */
export const sentenceCase = (t: string): string =>
  t
    .split('\n')
    .map((l) => {
      const s = l.toLowerCase();
      return s ? s[0].toUpperCase() + s.slice(1) : s;
    })
    .join('\n');

type PoolCard = {
  tag: string;
  title: string;
  body: string;
  cat: string;
  w: number;
  win?: boolean;
  score?: number;
};

export function buildDeck(
  wx: Wx | null,
  userName: string,
  nowM: number,
): DeckCard[] {
  const x = wx;
  const N = userName;
  const cards: DeckCard[] = [];

  // Opening card: hello — its `title` is the greeting, rendered in the insight
  // block's title slot like every other card (there is no separate greeting line).
  const hourLabel =
    nowM < 300
      ? 'Still up'
      : nowM < 720
        ? 'Good morning'
        : nowM < 1020
          ? 'Good afternoon'
          : 'Good evening';
  const skyLine = x
    ? `Your sky${x.city ? ' over ' + x.city : ''}: ${x.temp}°, ${x.desc}.` +
      (x.polar
        ? ''
        : ` ${nowM < x.sunsetM ? 'Sunset at' : 'Sunset was at'} ${fmtM(
            x.sunsetM,
          )}.`) +
      ' The UltraSphere read it a moment ago.'
    : 'The UltraSphere is running on your clock alone this time. Lend it your sky on the next visit and it gets sharper.';
  cards.push({
    tag: 'Hello',
    title: `${hourLabel}${N ? ', ' + N : ''}.`,
    body: skyLine,
    cat: 'hello',
  });

  const pool: PoolCard[] = [];
  const add = (cond: boolean, w: number, card: Omit<PoolCard, 'w'>) => {
    if (cond) pool.push({ ...card, w });
  };
  const sun = !!x && !x.polar; // sun-anchored cards need a real sunrise/sunset
  const morning =
    sun && x
      ? nowM >= x.sunriseM && nowM <= x.sunriseM + 150
      : !x && nowM >= 360 && nowM <= 570;
  const nightGate = nowM < 270; // before 4:30 am
  const ov = x && x.city ? x.city : 'your head'; // "over ___" phrasings

  if (nightGate) {
    // The night gate outranks everything, like the app.
    pool.push({
      w: 10,
      tag: 'Sleep',
      title: `It is ${fmtM(nowM)}${x && x.city ? ' in ' + x.city : ''}.`,
      body: 'Nothing on this screen beats sleep at this hour. Put the phone down and let the night do its work. The UltraSphere will be brighter in the morning, and so will you.',
      cat: 'sleep',
    });
    pool.push({
      w: 6,
      tag: 'Breathe',
      title: 'Take six slow breaths.',
      body: 'Longer out than in. It is the fastest lever you own on a racing mind, and it works lying down.',
      cat: 'universal',
    });
    pool.push({
      w: 5,
      tag: 'Deep sleep',
      title: 'Your brain rinses itself at night.',
      body: 'In deep sleep, fluid flushes the metabolic waste of the day. Most of that clean-up happens while you are under, so go get under.',
      cat: 'universal',
    });
  } else {
    if (x) {
      add(morning && !x.isRain && !x.isOvercast, 9, {
        win: true,
        tag: 'First light',
        title: 'Catch the first hours.',
        body: `Morning light over ${ov} runs in the thousands of lux. Your brightest room manages a few hundred. Take ten minutes outside before ${fmtM(
          x.sunriseM + 150,
        )} and set the master clock the rest of your body follows.`,
        cat: 'light',
      });
      add(morning && x.isOvercast, 9, {
        win: true,
        tag: 'Get light',
        title: 'Grey still counts.',
        body: `${x.cloud}% cloud over ${ov}, and the sky still beats your brightest room by ten times. Step out. Your body can tell the difference.`,
        cat: 'light',
      });
      add(morning && x.isRain, 8, {
        win: true,
        tag: 'Get light',
        title: 'Rain light still works.',
        body: `Even through rain, the sky over ${ov} out-brightens your ceiling many times over. Take a few minutes in a doorway or under an umbrella and start your clock.`,
        cat: 'light',
      });
      add(
        sun &&
          nowM >= x.sunsetM - 75 &&
          nowM < x.sunsetM - 4 &&
          !x.isRain &&
          !x.isStorm &&
          !x.isFog &&
          !x.isSnow,
        10,
        {
          win: true,
          tag: 'Golden hour',
          title: 'The softest light is leaving.',
          body: `Golden hour over ${ov} ends at ${fmtM(
            x.sunsetM,
          )}. Take a slow walk in it. It lowers stress and hands your body clock a clean end-of-day marker.`,
          cat: 'light',
        },
      );
      add(sun && nowM >= x.sunsetM && nowM <= 1380, 8, {
        win: true,
        tag: 'Wind down',
        title: `The sun clocked out at ${fmtM(x.sunsetM)}.`,
        body: 'Every bright screen from here on argues that it is midday. Dim the room a little and let your brain believe the sky.',
        cat: 'sleep',
      });
      add(sun && x.feels >= 28 && nowM >= x.sunsetM && nowM <= 1439, 9, {
        tag: 'Cool down',
        title: `Tonight is holding ${x.temp}°.`,
        body: 'Your core has to drop about 1°C to let sleep in. Cool the room, choose cotton, and give the drop a head start.',
        cat: 'heat',
      });
      add(x.feels >= 32 && x.isDay, 8, {
        tag: 'Hydrate',
        title: `It feels like ${x.feels}° out there.`,
        body: 'Hard effort in this heat can cost you around a litre of water an hour. Drink before you feel thirsty. Thirst reports late.',
        cat: 'heat',
      });
      add(x.temp <= 5 && nowM >= 1140, 8, {
        tag: 'Warm up',
        title: 'Socks are sleep technology.',
        body: 'Cold feet keep a sleeping brain on alert. Warm your extremities and the vessels that release core heat open up. Sleep follows the drop.',
        cat: 'sleep',
      });
      add(x.temp <= 5 && x.isDay && nowM < 1140, 6, {
        tag: 'Cold walk',
        title: 'A cold walk pays twice.',
        body: `${x.temp}° air wakes brown fat and sharpens attention for about an hour afterwards. Bundle up, step out, come back warm.`,
        cat: 'weather',
      });
      add(x.isRain && !x.isStorm && (nowM >= 1020 || !x.isDay), 8, {
        tag: 'Rain sound',
        title: 'Rain is a sleep machine.',
        body: `Steady rain over ${ov} is natural pink noise. It masks the sound spikes that fragment sleep. Keep it within earshot when you turn in.`,
        cat: 'weather',
      });
      add(x.isStorm, 9, {
        tag: 'Go gentle',
        title: 'Storm air is heavy air.',
        body: 'Storms ride on a barometric dip, and some bodies read it as heaviness or a dull head. Plan a gentler day and keep water close.',
        cat: 'weather',
      });
      add(x.isSnow && x.isDay, 9, {
        tag: 'Snow light',
        title: 'Snow light is a gift.',
        body: 'Fresh snow reflects most of the daylight that hits it, nearly doubling what reaches your eyes. Take a short walk out there. It is a strong clock signal.',
        cat: 'weather',
      });
      add(x.isFog && x.isDay, 8, {
        tag: 'Get light',
        title: 'The sky is running late.',
        body: `Fog over ${ov} delays your light dose. It does not cancel it. Twenty minutes outside still tells your clock the day began.`,
        cat: 'light',
      });
      add(x.uv >= 7 && x.isDay, 8, {
        tag: 'Find shade',
        title: `UV is running at ${x.uv}${x.city ? ' over ' + x.city : ''}.`,
        body: 'Take your daylight early or in open shade. The clock benefit stays. The burn does not.',
        cat: 'weather',
      });
      add(x.wind >= 25, 6, {
        tag: 'Wind',
        title: `Wind at ${x.wind} km/h.`,
        body: 'If it holds into the night, gusts will poke at light sleep. Close the window on the wind side or run steady noise to even the field.',
        cat: 'weather',
      });
      add(x.humidity >= 80 && x.feels >= 26, 7, {
        tag: 'Slow down',
        title: `${x.humidity}% humidity out there.`,
        body: 'Sweat evaporates slowly in air this wet, so cooling stalls. Ease your pace and let a fan move the air for you.',
        cat: 'heat',
      });
      add(sun, 5, {
        tag: 'Light budget',
        title: `${x.city || 'Your sky'} gets ${fmtDaylight(
          x.daylightS,
        )} of light today.`,
        body: `Sunrise ${fmtM(x.sunriseM)}, sunset ${fmtM(
          x.sunsetM,
        )}. That is the whole budget. Spend at least ten minutes of it outside.`,
        cat: 'light',
      });
      add(sun && (nowM >= x.sunsetM + 60 || (!x.isDay && nowM > 1020)), 5, {
        tag: 'First light',
        title: `Tomorrow starts at ${fmtM(x.sunrise2M)}.`,
        body: 'Your first ten minutes of light decide how the whole day runs. Set nothing else. Set that.',
        cat: 'light',
      });
      add(x.isClear && x.isDay, 3, {
        tag: 'Sunlight',
        title: 'Your light is eight minutes old.',
        body: `The photons landing on ${ov} left the sun eight minutes ago. Step out and catch a few before they bounce away.`,
        cat: 'universal',
      });
    }
    // Clock-only insights, always available.
    add(nowM >= 780 && nowM < 960, x && x.isClear ? 7 : 6, {
      win: true,
      tag: 'Beat the dip',
      title: 'Your dip is on schedule.',
      body: "Energy sags five to eight hours after waking, by design. Take ten minutes of daylight. It lifts you without borrowing from tonight's sleep.",
      cat: 'light',
    });
    add(nowM >= 840 && nowM < 1140, 7, {
      win: true,
      tag: 'Cut caffeine',
      title: 'That coffee has a half-life.',
      body: 'Caffeine takes about six hours to halve, so a late-afternoon cup is still working your receptors at midnight. Make the next one your last.',
      cat: 'sleep',
    });
    add(nowM >= 1320, 8, {
      win: true,
      tag: 'Head to bed',
      title: 'Your wind-down window is open.',
      body: 'Core temperature starts its dive about two hours before natural sleep. Head toward bed while the drop is on your side.',
      cat: 'sleep',
    });
    add(true, 4, {
      tag: 'Your heart',
      title: '100,000 beats a day.',
      body: `${
        N ? N + ', your' : 'Your'
      } heart beats about 100,000 times a day, and you feel almost none of them. Sit still for ten of them and listen. Something on your finger soon will.`,
      cat: 'universal',
    });
    add(true, 4, {
      tag: 'Breathe',
      title: '22,000 breaths, nearly all automatic.',
      body: 'Take six of them slowly, on purpose, longer out than in. It is the fastest lever you own on stress.',
      cat: 'universal',
    });
    add(true, 4, {
      tag: 'Deep sleep',
      title: 'Your brain rinses itself at night.',
      body: 'In deep sleep, fluid flushes the metabolic waste of the day. Most of that clean-up happens while you are under. Protect the deep hours.',
      cat: 'universal',
    });
    add(true, 3, {
      tag: 'Body clock',
      title: 'Every cell keeps time.',
      body: 'Trillions of clocks, and they all set themselves by morning light and steady meals. Keep both on schedule and everything downstream gets easier.',
      cat: 'universal',
    });
  }

  // Pick 2: weight + jitter, max 2 per category, no repeated tags.
  pool.forEach((c) => {
    c.score = c.w + (c.win ? 10 : 0) + Math.random() * 1.5;
  });
  pool.sort((a, b) => (b.score ?? 0) - (a.score ?? 0));
  const catCount: Record<string, number> = {};
  const usedTags = new Set(cards.map((c) => c.tag));
  for (const c of pool) {
    if (cards.length >= 3) break;
    if (usedTags.has(c.tag)) continue;
    catCount[c.cat] = catCount[c.cat] || 0;
    if (catCount[c.cat] >= 2) continue;
    catCount[c.cat] += 1;
    usedTags.add(c.tag);
    cards.push({ tag: c.tag, title: c.title, body: c.body, cat: c.cat });
  }

  cards.push({
    tag: 'Emerald',
    title: 'A new Ultrahuman experience.',
    body: 'Built around what your body is already saying. The UltraSphere arrives soon. Keep an eye on the sky.',
    cat: 'end',
  });
  return cards;
}
